<template>
  <div class="card">
    <div class="card-header">
      <h2 class="card-header-title">Modifier la configuration</h2>
    </div>

    <div class="card-content">
      <b-message type="is-warning" has-icon>
        Les changements apportés <strong>ne seront pas appliqués immédiatement</strong>.
        <br />
        La mise à jour prendra effet une fois que la balises aura récupéré
        <strong>automatiquement</strong> sa configuration (voir estimation ci-dessus). Il est
        possible de <strong>forcer la mise à jour</strong> en resynchronisant manuellement la
        balises, ce qui implique l'envoi de <strong>SMS</strong> (réception non garantie).<br />
        Pour garantir la prise en compte immédiate, il est recommandé de
        <strong>redémarrer manuellement</strong> la balise.
      </b-message>

      <o-table :items="modes" :loading="loading" :draggable="false" :show-total="false">
        <b-table-column v-slot="{ row }" field="title" label="Mode">
          <span class="has-text-weight-bold">
            <b-icon :icon="row.icon" size="is-small" type="is-primary" class="mr-2" />{{
              row.title
            }}
          </span>
          <b-tooltip :label="row.hint" position="is-right" multilined>
            <b-icon icon="circle-info" size="is-small" class="has-text-grey-light ml-1" />
          </b-tooltip>
        </b-table-column>

        <b-table-column v-slot="{ row }" field="intervalField" label="Intervalle (s)" numeric>
          <o-input-number
            lazy
            expanded
            controls-alignment="right"
            controls-position="compact"
            :number.sync="fields[row.intervalField].value"
            :min="row.intervalMin"
            :max="row.intervalMax"
            :min-error="row.intervalMinError"
            :max-error="row.intervalMaxError"
            @error="fields[row.intervalField].error = $event"
          />
        </b-table-column>

        <b-table-column v-slot="{ row }" field="pointsField" label="Points par envoi" numeric>
          <o-input-number
            v-if="row.pointsField"
            lazy
            expanded
            controls-alignment="right"
            controls-position="compact"
            :number.sync="fields[row.pointsField].value"
            :min="MIN_POINTS_PER_MESSAGE"
            :max="MAX_POINTS_PER_MESSAGE"
            :min-error="pointsPerMessageError.min"
            :max-error="pointsPerMessageError.max"
            @error="fields[row.pointsField].error = $event"
          />
          <span v-else class="has-text-grey">{{ MIN_POINTS_PER_MESSAGE }}</span>
        </b-table-column>

        <b-table-column v-slot="{ row }" field="sendInterval" label="Envoi" numeric>
          <b-tag v-if="row.sendInterval" type="is-info is-light">{{ row.sendInterval }}</b-tag>
        </b-table-column>
      </o-table>

      <b-field
        label="Activer la veille"
        message="La balise réduit sa fréquence d'envoi lorsqu'elle est immobile. La désactiver augmente la consommation de batterie."
      >
        <b-switch v-model="fields.sleepEnabled.value" type="is-success">
          {{ fields.sleepEnabled.value ? 'Activée' : 'Désactivée' }}
        </b-switch>
      </b-field>

      <b-field>
        <b-button type="is-primary" :loading="loading" :disabled="disabledSubmit" @click="submit">
          Modifier
        </b-button>
      </b-field>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import OInputNumber from '@components/InputNumber.vue'
import OTable from '@components/Table.vue'
import { formatDuration } from '@helpers/formatDuration'
import {
  MAX_POINTS_PER_MESSAGE,
  MIN_POINTS_PER_MESSAGE,
} from '@constants/queclinkTrackerConfig/pointsPerMessage'

const MIN_INTERVAL = 1

export default {
  name: 'QueclinkTrackerConfigForm',

  components: {
    OInputNumber,
    OTable,
  },

  props: {
    queclinkTrackerConfig: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      fields: {
        performanceIntervalSeconds: {
          value: this.queclinkTrackerConfig.performanceIntervalSeconds,
          error: null,
        },
        performancePointsPerMessage: {
          value: this.queclinkTrackerConfig.performancePointsPerMessage,
          error: null,
        },
        defaultIntervalSeconds: {
          value: this.queclinkTrackerConfig.defaultIntervalSeconds,
          error: null,
        },
        defaultPointsPerMessage: {
          value: this.queclinkTrackerConfig.defaultPointsPerMessage,
          error: null,
        },
        idleIntervalSeconds: {
          value: this.queclinkTrackerConfig.idleIntervalSeconds,
          error: null,
        },
        sleepEnabled: {
          value: this.queclinkTrackerConfig.sleepEnabled,
          error: null,
        },
      },
    }
  },

  watch: {
    queclinkTrackerConfig: {
      handler(newConfig, oldConfig) {
        if (newConfig.performanceIntervalSeconds !== oldConfig?.performanceIntervalSeconds) {
          this.fields.performanceIntervalSeconds.value = newConfig.performanceIntervalSeconds
        }

        if (newConfig.performancePointsPerMessage !== oldConfig?.performancePointsPerMessage) {
          this.fields.performancePointsPerMessage.value = newConfig.performancePointsPerMessage
        }

        if (newConfig.defaultIntervalSeconds !== oldConfig?.defaultIntervalSeconds) {
          this.fields.defaultIntervalSeconds.value = newConfig.defaultIntervalSeconds
        }

        if (newConfig.defaultPointsPerMessage !== oldConfig?.defaultPointsPerMessage) {
          this.fields.defaultPointsPerMessage.value = newConfig.defaultPointsPerMessage
        }

        if (newConfig.idleIntervalSeconds !== oldConfig?.idleIntervalSeconds) {
          this.fields.idleIntervalSeconds.value = newConfig.idleIntervalSeconds
        }

        if (newConfig.sleepEnabled !== oldConfig?.sleepEnabled) {
          this.fields.sleepEnabled.value = newConfig.sleepEnabled
        }
      },
    },
  },

  computed: {
    MIN_POINTS_PER_MESSAGE: () => MIN_POINTS_PER_MESSAGE,

    MAX_POINTS_PER_MESSAGE: () => MAX_POINTS_PER_MESSAGE,

    disabledSubmit() {
      return this.loading || this.hasError
    },

    hasError() {
      return Object.values(this.fields).some((field) => field.error)
    },

    minIntervalError() {
      return `Minimum ${MIN_INTERVAL} seconde.`
    },

    pointsPerMessageError() {
      return {
        min: `Minimum ${MIN_POINTS_PER_MESSAGE} point.`,
        max: `Maximum ${MAX_POINTS_PER_MESSAGE} points par envoi.`,
      }
    },

    modes() {
      return [
        {
          key: 'idle',
          icon: 'moon',
          title: 'Repos',
          hint: 'Pour les phases de distribution ou de repos.',
          intervalField: 'idleIntervalSeconds',
          intervalMin: this.fields.defaultIntervalSeconds.value,
          intervalMax: undefined,
          intervalMinError: "Doit être supérieur à l'intervalle défaut.",
          intervalMaxError: null,
          pointsField: null,
          sendInterval: formatDuration(this.fields.idleIntervalSeconds.value),
        },
        {
          key: 'default',
          icon: 'location-arrow',
          title: 'Défaut',
          hint: 'Équilibré entre précision et consommation.',
          intervalField: 'defaultIntervalSeconds',
          intervalMin: this.fields.performanceIntervalSeconds.value,
          intervalMax: this.fields.idleIntervalSeconds.value,
          intervalMinError: "Doit être supérieur à l'intervalle performance.",
          intervalMaxError: null,
          pointsField: 'defaultPointsPerMessage',
          sendInterval: formatDuration(
            this.fields.defaultIntervalSeconds.value * this.fields.defaultPointsPerMessage.value,
          ),
        },
        {
          key: 'performance',
          icon: 'tachometer-alt',
          title: 'Performance',
          hint: 'Fréquence élevée, pour une trace détaillée du parcours.',
          intervalField: 'performanceIntervalSeconds',
          intervalMin: MIN_INTERVAL,
          intervalMax: this.fields.defaultIntervalSeconds.value,
          intervalMinError: this.minIntervalError,
          intervalMaxError: "Doit être inférieur à l'intervalle défaut.",
          pointsField: 'performancePointsPerMessage',
          sendInterval: formatDuration(
            this.fields.performanceIntervalSeconds.value *
              this.fields.performancePointsPerMessage.value,
          ),
        },
      ]
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    async submit() {
      this.loading = true

      try {
        const queclinkTrackerConfig = await this.$services.queclinkTrackerConfigService.update({
          ...this.queclinkTrackerConfig,
          performanceIntervalSeconds: this.fields.performanceIntervalSeconds.value,
          performancePointsPerMessage: this.fields.performancePointsPerMessage.value,
          defaultIntervalSeconds: this.fields.defaultIntervalSeconds.value,
          defaultPointsPerMessage: this.fields.defaultPointsPerMessage.value,
          idleIntervalSeconds: this.fields.idleIntervalSeconds.value,
          sleepEnabled: this.fields.sleepEnabled.value,
        })

        this.$emit('update:queclinkTrackerConfig', queclinkTrackerConfig)

        this.addToastMessage({
          text: 'Configuration mise à jour',
          type: 'is-success',
        })
      } catch (err) {
        this.addToastMessage({
          text: 'Une erreur interne est survenue.',
          type: 'is-danger',
        })
        console.error(err)
      }

      this.loading = false
    },
  },
}
</script>
